'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import StepBrandInfo from './StepBrandInfo';
import StepObjective from './StepObjective';
import StepAudience from './StepAudience';
import StepContentMix from './StepContentMix';
import StepBudget from './StepBudget';
import CampaignRecommendations from './CampaignRecommendations';

export type CampaignFormData = {
  brandName: string;
  brandCategory: string;
  contactName: string;
  contactEmail: string;
  objective: string;
  objectiveDetail: string;
  targetGender: string;
  targetAgeMin: number;
  targetAgeMax: number;
  targetWilaya: string[];
  targetLanguage: string[];
  creatorCategories: string[];
  platforms: string[];
  contentReels: number;
  contentStories: number;
  contentTiktok: number;
  contentUGC: number;
  budgetTotal: number;
  budgetPerCreator: number;
  timeline: string;
  certifiedOnly: boolean;
};

const steps = [
  { id: 'step-brand', number: 1, label: 'Marque', icon: 'BuildingOffice2Icon' },
  { id: 'step-objective', number: 2, label: 'Objectif', icon: 'FlagIcon' },
  { id: 'step-audience', number: 3, label: 'Audience', icon: 'UsersIcon' },
  { id: 'step-content', number: 4, label: 'Contenus', icon: 'FilmIcon' },
  { id: 'step-budget', number: 5, label: 'Budget', icon: 'CurrencyDollarIcon' },
];

export default function CampaignBuilderClient() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<CampaignFormData>({
    defaultValues: {
      brandName: '',
      brandCategory: '',
      contactName: '',
      contactEmail: '',
      objective: '',
      objectiveDetail: '',
      targetGender: '',
      targetAgeMin: 18,
      targetAgeMax: 35,
      targetWilaya: ['Alger'],
      targetLanguage: ['Darija'],
      creatorCategories: [],
      platforms: ['instagram'],
      contentReels: 5,
      contentStories: 15,
      contentTiktok: 2,
      contentUGC: 0,
      budgetTotal: 500000,
      budgetPerCreator: 120000,
      timeline: '3 semaines',
      certifiedOnly: false,
    },
  });

  const next = async () => {
    const valid = await form.trigger();
    if (valid) setCurrentStep(s => Math.min(s + 1, 5));
  };

  const prev = () => setCurrentStep(s => Math.max(s - 1, 1));

  const onSubmit = form.handleSubmit(() => {
    // Backend integration point: POST /api/campaigns with form data
    setSubmitted(true);
  });

  if (submitted) {
    return (
      <div className="max-w-4xl mx-auto">
        <CampaignRecommendations formData={form.getValues()} onReset={() => { setSubmitted(false); setCurrentStep(1); form.reset(); }} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-700 text-foreground">Campaign Builder</h1>
        <p className="text-sm text-muted-foreground mt-1">Définissez votre brief et obtenez les créateurs les plus adaptés</p>
      </div>

      {/* Progress Steps */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between relative">
          <div className="absolute left-0 right-0 top-5 h-0.5 bg-border mx-8" />
          <div
            className="absolute left-0 top-5 h-0.5 bg-primary transition-all duration-500 mx-8"
            style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
          />
          {steps.map((step) => (
            <div key={step.id} className="relative flex flex-col items-center gap-2 z-10">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                step.number < currentStep
                  ? 'bg-primary border-primary'
                  : step.number === currentStep
                  ? 'bg-primary/20 border-primary' :'bg-card border-border'
              }`}>
                {step.number < currentStep ? (
                  <Icon name="CheckIcon" size={16} className="text-white" />
                ) : (
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={16} className={step.number === currentStep ? 'text-primary' : 'text-muted-foreground'} />
                )}
              </div>
              <span className={`text-xs font-600 ${step.number === currentStep ? 'text-primary' : step.number < currentStep ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-card border border-border rounded-xl p-8">
        <form onSubmit={onSubmit}>
          {currentStep === 1 && <StepBrandInfo form={form} />}
          {currentStep === 2 && <StepObjective form={form} />}
          {currentStep === 3 && <StepAudience form={form} />}
          {currentStep === 4 && <StepContentMix form={form} />}
          {currentStep === 5 && <StepBudget form={form} />}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <button
              type="button"
              onClick={prev}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-600 text-muted-foreground hover:text-foreground hover:bg-muted transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeftIcon" size={16} />
              Précédent
            </button>

            <span className="text-xs text-muted-foreground">Étape {currentStep} sur 5</span>

            {currentStep < 5 ? (
              <button
                type="button"
                onClick={next}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2.5 rounded-lg text-sm font-600 transition-all active:scale-95"
              >
                Suivant
                <Icon name="ChevronRightIcon" size={16} />
              </button>
            ) : (
              <button
                type="submit"
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2.5 rounded-lg text-sm font-700 transition-all active:scale-95"
              >
                <Icon name="SparklesIcon" size={16} />
                Trouver les créateurs
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}